import { Mail, Phone, Users } from 'lucide-react'

import DNButton from 'src/components/Buttons/Button/DNButton'
import ContactSourceBadge from 'src/components/Inputs/CarrierAutocomplete/ContactSourceBadge'
import {
  KnownContact,
  formatContactCount,
} from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import { formatPhone } from 'src/lib/phone.utils'

export interface KnownContactsBlockProps {
  dotNumber: string
  /** Reachable contacts in directory order; the first is the one offered. */
  contacts: KnownContact[]
  onUse: (contact: KnownContact) => void
  saving?: boolean
}

/**
 * Shown in the employment drawer's read mode when the entry has no contact
 * but the directory holds some for its DOT: the count, and the top-ranked
 * contact with a "Use" button that saves it onto the entry in one click.
 */
const KnownContactsBlock = ({
  dotNumber,
  contacts,
  onUse,
  saving = false,
}: KnownContactsBlockProps) => {
  const top = contacts[0]
  if (!top) return null

  return (
    <div className="flex flex-col gap-2" data-testid="known-contacts-block">
      <div className="flex items-center gap-1.5 text-xs text-doubleNickel-gray-600">
        <Users size={12} className="shrink-0 text-doubleNickel-gray-500" />
        <span>
          <span className="font-semibold text-doubleNickel-gray-700">
            {formatContactCount(contacts.length)}
          </span>{' '}
          for DOT {dotNumber}
        </span>
      </div>

      <div className="flex flex-row items-center justify-between gap-3 rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-25 p-2.5">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs font-semibold text-doubleNickel-gray-800">
              {top.contactName || 'Unnamed Contact'}
            </span>
            <ContactSourceBadge source={top.source} />
          </div>
          {top.phoneNumber && (
            <span className="flex items-center gap-1.5 text-xs text-doubleNickel-gray-600">
              <Phone
                size={11}
                className="shrink-0 text-doubleNickel-gray-400"
                aria-hidden
              />
              {formatPhone(top.phoneNumber)}
            </span>
          )}
          {top.email && (
            <span className="flex min-w-0 items-center gap-1.5 text-xs text-doubleNickel-gray-600">
              <Mail
                size={11}
                className="shrink-0 text-doubleNickel-gray-400"
                aria-hidden
              />
              <span className="truncate">{top.email}</span>
            </span>
          )}
        </div>
        <DNButton
          variant="primary"
          className="shrink-0 px-3 py-1"
          onClick={() => onUse(top)}
          loading={saving}
          disabled={saving}
        >
          Use
        </DNButton>
      </div>
    </div>
  )
}

export default KnownContactsBlock
